// src/views/ContratosView.jsx
import React, { useEffect, useMemo, useState } from "react";
import { getContratos, deleteContrato } from "../services/api";
import { Link } from "react-router-dom";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const TIPO_TELEFONIA = "TELEFONIA_INTERNET";
const TIPO_SUSCRIPCION = "SUSCRIPCION_DIGITAL";

function normalize(v) {
  return (v ?? "").toString().trim();
}

function money(v) {
  if (v === null || v === undefined || v === "") return "-";
  const n = Number(v);
  if (Number.isNaN(n)) return `$${v}`;
  return n.toLocaleString("es-MX", { style: "currency", currency: "MXN" });
}

function formatDate(v) {
  const s = normalize(v);
  if (!s) return "-";
  return s;
}

function getTipoServicio(c) {
  return c.tipoServicio || TIPO_TELEFONIA;
}

function getTipoLabel(c) {
  return getTipoServicio(c) === TIPO_SUSCRIPCION
    ? "Suscripción digital"
    : "Telefonía / Internet";
}

function getTotalBase(c) {
  const raw = c.totalPagar ?? c.costo ?? 0;
  const n = Number(raw);
  return Number.isNaN(n) ? 0 : n;
}

function getPeriodicidad(c) {
  return normalize(c.periodicidad || "mensual").toLowerCase();
}

function calcularCostoMensual(c) {
  const costo = getTotalBase(c);
  const periodicidad = getPeriodicidad(c);

  switch (periodicidad) {
    case "anual":
      return costo / 12;
    case "semestral":
      return costo / 6;
    case "trimestral":
      return costo / 3;
    case "unico":
      return 0;
    case "mensual":
    default:
      return costo;
  }
}

function calcularCostoAnual(c) {
  return calcularCostoMensual(c) * 12;
}

function servicioPrincipal(c) {
  if (getTipoServicio(c) === TIPO_SUSCRIPCION) {
    return normalize(c.servicio || c.plataforma || c.compania) || "-";
  }

  return normalize(c.oficina || c.ubicacion || c.compania) || "-";
}

function responsablePrincipal(c) {
  if (getTipoServicio(c) === TIPO_SUSCRIPCION) {
    return normalize(c.responsableInterno || c.propietario) || "-";
  }

  return normalize(c.propietario) || "-";
}

function administradorPrincipal(c) {
  if (getTipoServicio(c) === TIPO_SUSCRIPCION) {
    return normalize(c.correoAdministrador || c.correo) || "-";
  }

  return normalize(c.correo || c.telefono) || "-";
}

function fechaClave(c) {
  return normalize(c.fechaRenovacion || c.fechaPago);
}

function estaPorRenovar(c, dias = 45) {
  const fecha = normalize(c.fechaRenovacion);
  if (!fecha) return false;

  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);

  const objetivo = new Date(`${fecha}T00:00:00`);
  if (Number.isNaN(objetivo.getTime())) return false;

  const diff = objetivo.getTime() - hoy.getTime();
  const diffDias = diff / (1000 * 60 * 60 * 24);

  return diffDias >= 0 && diffDias <= dias;
}

const SORTERS = {
  tipoServicio: (a, b) => getTipoLabel(a).localeCompare(getTipoLabel(b), "es"),
  ubicacion: (a, b) =>
    normalize(a.ubicacion).localeCompare(normalize(b.ubicacion), "es"),
  propietario: (a, b) =>
    responsablePrincipal(a).localeCompare(responsablePrincipal(b), "es"),
  oficina: (a, b) => servicioPrincipal(a).localeCompare(servicioPrincipal(b), "es"),
  correo: (a, b) =>
    administradorPrincipal(a).localeCompare(administradorPrincipal(b), "es"),
  fechaPago: (a, b) => fechaClave(a).localeCompare(fechaClave(b), "es"),
  totalPagar: (a, b) => calcularCostoMensual(a) - calcularCostoMensual(b),
  compania: (a, b) =>
    normalize(a.compania).localeCompare(normalize(b.compania), "es"),
  numeroCuenta: (a, b) =>
    normalize(a.numeroCuenta).localeCompare(normalize(b.numeroCuenta), "es"),
  periodicidad: (a, b) => getPeriodicidad(a).localeCompare(getPeriodicidad(b), "es"),
  estatus: (a, b) => normalize(a.estatus).localeCompare(normalize(b.estatus), "es"),
};

function pad2(n) {
  return String(n).padStart(2, "0");
}

function filenameStamp() {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}_${pad2(
    d.getHours()
  )}${pad2(d.getMinutes())}`;
}

function escapeCsvCell(v) {
  const s = normalize(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function downloadBlob(content, mimeType, filename) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");

  a.href = url;
  a.download = filename;

  document.body.appendChild(a);
  a.click();
  a.remove();

  URL.revokeObjectURL(url);
}

export default function ContratosView() {
  const [contratos, setContratos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [query, setQuery] = useState("");
  const [filterCompania, setFilterCompania] = useState("Todas");
  const [filterTipo, setFilterTipo] = useState("Todos");
  const [sortKey, setSortKey] = useState("fechaPago");
  const [sortDir, setSortDir] = useState("desc");
  const [revealed, setRevealed] = useState(() => new Set());
  const [deletingId, setDeletingId] = useState(null);

  const cargarContratos = async () => {
    setLoading(true);
    setErrorMsg("");

    try {
      const res = await getContratos();
      setContratos(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error al cargar contratos:", err);
      setErrorMsg("No se pudieron cargar los contratos y suscripciones. Reintenta.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarContratos();
  }, []);

  const companias = useMemo(() => {
    const set = new Set(contratos.map((c) => normalize(c.compania)).filter(Boolean));
    return ["Todas", ...Array.from(set).sort((a, b) => a.localeCompare(b, "es"))];
  }, [contratos]);

  const filtered = useMemo(() => {
    const q = normalize(query).toLowerCase();

    return contratos
      .filter((c) => {
        if (filterCompania !== "Todas" && normalize(c.compania) !== filterCompania) {
          return false;
        }

        if (filterTipo !== "Todos" && getTipoServicio(c) !== filterTipo) {
          return false;
        }

        if (!q) return true;

        const haystack = [
          getTipoLabel(c),
          c.ubicacion,
          c.propietario,
          c.oficina,
          c.correo,
          c.telefono,
          c.compania,
          c.numeroCuenta,
          c.proveedor,
          c.servicio,
          c.plataforma,
          c.plan,
          c.periodicidad,
          c.fechaRenovacion,
          c.responsableInterno,
          c.correoAdministrador,
          c.metodoPago,
          c.estatus,
          c.notas,
        ]
          .map((x) => normalize(x).toLowerCase())
          .join(" ");

        return haystack.includes(q);
      })
      .sort((a, b) => {
        const fn = SORTERS[sortKey] || (() => 0);
        const out = fn(a, b);
        return sortDir === "asc" ? out : -out;
      });
  }, [contratos, query, filterCompania, filterTipo, sortKey, sortDir]);

  const resumen = useMemo(() => {
    return filtered.reduce(
      (acc, c) => {
        const tipo = getTipoServicio(c);
        const mensual = calcularCostoMensual(c);
        const anual = calcularCostoAnual(c);

        acc.totalMensual += mensual;
        acc.totalAnual += anual;

        if (tipo === TIPO_SUSCRIPCION) {
          acc.suscripciones += 1;
          acc.totalSuscripcionesMensual += mensual;
          acc.totalSuscripcionesAnual += anual;
        } else {
          acc.telefoniaInternet += 1;
          acc.totalTelefoniaMensual += mensual;
          acc.totalTelefoniaAnual += anual;
        }

        if (estaPorRenovar(c)) {
          acc.proximasRenovaciones += 1;
        }

        return acc;
      },
      {
        totalMensual: 0,
        totalAnual: 0,
        suscripciones: 0,
        telefoniaInternet: 0,
        totalSuscripcionesMensual: 0,
        totalSuscripcionesAnual: 0,
        totalTelefoniaMensual: 0,
        totalTelefoniaAnual: 0,
        proximasRenovaciones: 0,
      }
    );
  }, [filtered]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "totalPagar" ? "desc" : "asc");
    }
  };

  const eliminar = async (id) => {
    const ok = window.confirm(
      "¿Eliminar este servicio? Esta acción no se puede deshacer."
    );

    if (!ok) return;

    try {
      setDeletingId(id);
      await deleteContrato(id);
      await cargarContratos();
    } catch (err) {
      console.error("Error al eliminar:", err);
      alert("Error al eliminar servicio");
    } finally {
      setDeletingId(null);
    }
  };

  const toggleReveal = (id) => {
    setRevealed((prev) => {
      const next = new Set(prev);

      if (next.has(id)) next.delete(id);
      else next.add(id);

      return next;
    });
  };

  const exportColumns = [
    { header: "Tipo", key: "tipo" },
    { header: "Compañía / Plataforma", key: "compania" },
    { header: "Servicio / Oficina", key: "servicio" },
    { header: "Plan", key: "plan" },
    { header: "Responsable", key: "responsable" },
    { header: "Administrador / Contacto", key: "administrador" },
    { header: "Periodicidad", key: "periodicidad" },
    { header: "Costo original", key: "costoOriginal" },
    { header: "Mensual estimado", key: "mensual" },
    { header: "Anual estimado", key: "anual" },
    { header: "Fecha pago", key: "fechaPago" },
    { header: "Fecha renovación", key: "fechaRenovacion" },
    { header: "Estatus", key: "estatus" },
    { header: "No. cuenta / ID", key: "numeroCuenta" },
    { header: "Notas", key: "notas" },
  ];

  const exportRows = useMemo(() => {
    return filtered.map((c) => ({
      tipo: getTipoLabel(c),
      compania: normalize(c.compania) || "-",
      servicio: servicioPrincipal(c),
      plan: normalize(c.plan) || "-",
      responsable: responsablePrincipal(c),
      administrador: administradorPrincipal(c),
      periodicidad: normalize(c.periodicidad || "mensual") || "-",
      costoOriginal: money(getTotalBase(c)),
      mensual: money(calcularCostoMensual(c)),
      anual: money(calcularCostoAnual(c)),
      fechaPago: formatDate(c.fechaPago),
      fechaRenovacion: formatDate(c.fechaRenovacion),
      estatus: normalize(c.estatus || "ACTIVO") || "-",
      numeroCuenta: normalize(c.numeroCuenta) || "-",
      notas: normalize(c.notas) || "-",
    }));
  }, [filtered]);

  const exportCsv = () => {
    if (!exportRows.length) return alert("No hay datos para exportar.");

    const title = "Contratos y Suscripciones";

    const meta = [
      ["Reporte", title],
      ["Generado", new Date().toLocaleString("es-MX")],
      ["Filtro tipo", filterTipo === "Todos" ? "Todos" : filterTipo],
      ["Filtro compañía", filterCompania],
      ["Búsqueda", query ? query : "(sin búsqueda)"],
      ["Registros", String(exportRows.length)],
      ["Total mensual estimado", money(resumen.totalMensual)],
      ["Total anual estimado", money(resumen.totalAnual)],
      ["Telefonía / Internet", String(resumen.telefoniaInternet)],
      ["Suscripciones digitales", String(resumen.suscripciones)],
      ["Próximas renovaciones", String(resumen.proximasRenovaciones)],
    ];

    const headers = exportColumns.map((c) => c.header);
    const lines = [];

    lines.push(...meta.map((row) => row.map(escapeCsvCell).join(",")));
    lines.push("");
    lines.push(headers.map(escapeCsvCell).join(","));

    exportRows.forEach((r) => {
      const row = exportColumns.map((col) => escapeCsvCell(r[col.key]));
      lines.push(row.join(","));
    });

    const csvContent = "\uFEFF" + lines.join("\n");
    downloadBlob(
      csvContent,
      "text/csv;charset=utf-8;",
      `contratos_suscripciones_${filenameStamp()}.csv`
    );
  };

  const exportPdf = () => {
  if (!filtered.length) return alert("No hay datos para exportar.");

  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 40;

  const generated = new Date().toLocaleString("es-MX");

  const suscripciones = filtered.filter((c) => getTipoServicio(c) === TIPO_SUSCRIPCION);
  const telefonia = filtered.filter((c) => getTipoServicio(c) === TIPO_TELEFONIA);

  const totalSuscripcionesMensual = suscripciones.reduce(
    (acc, c) => acc + calcularCostoMensual(c),
    0
  );

  const totalTelefoniaMensual = telefonia.reduce(
    (acc, c) => acc + calcularCostoMensual(c),
    0
  );

  const totalSuscripcionesAnual = suscripciones.reduce(
    (acc, c) => acc + calcularCostoAnual(c),
    0
  );

  const totalTelefoniaAnual = telefonia.reduce(
    (acc, c) => acc + calcularCostoAnual(c),
    0
  );

  const renovaciones = filtered.filter((c) => estaPorRenovar(c));

  const drawFooter = () => {
    const pageCount = doc.internal.getNumberOfPages();
    const page = doc.internal.getCurrentPageInfo().pageNumber;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(120);

    doc.text(
      `Página ${page} de ${pageCount}`,
      pageWidth - marginX,
      pageHeight - 18,
      { align: "right" }
    );

    doc.text(
      "Documento ejecutivo generado desde el sistema de inventario IP DEPOT.",
      marginX,
      pageHeight - 18
    );
  };

  const drawHeader = (title, subtitle = "") => {
    doc.setFillColor(17, 24, 39);
    doc.rect(0, 0, pageWidth, 54, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(255);
    doc.text(title, marginX, 32);

    if (subtitle) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(220);
      doc.text(subtitle, pageWidth - marginX, 32, { align: "right" });
    }

    doc.setTextColor(0);
  };

  const drawMetricCard = (x, y, w, h, label, value, sub = "") => {
    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(226, 232, 240);
    doc.roundedRect(x, y, w, h, 8, 8, "FD");

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(100);
    doc.text(label, x + 12, y + 17);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(17, 24, 39);
    doc.text(String(value), x + 12, y + 38);

    if (sub) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.setTextColor(100);
      doc.text(String(sub), x + 12, y + 53);
    }
  };

  const sectionTitle = (title, y, subtitle = "") => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(17, 24, 39);
    doc.text(title, marginX, y);

    if (subtitle) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(100);
      doc.text(subtitle, marginX, y + 14);
    }

    doc.setTextColor(0);
  };

  const checkPageSpace = (currentY, needed = 120) => {
    if (currentY + needed > pageHeight - 50) {
      drawFooter();
      doc.addPage();
      drawHeader("Contratos y Suscripciones", `Generado: ${generated}`);
      return 80;
    }

    return currentY;
  };

  // =========================
  // PORTADA / RESUMEN EJECUTIVO
  // =========================
  drawHeader("Contratos y Suscripciones", `Generado: ${generated}`);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(75);

  doc.text(
    "Resumen ejecutivo de gasto mensual, proyección anual, servicios digitales, telefonía e internet.",
    marginX,
    78
  );

  doc.text(
    `Filtro tipo: ${filterTipo === "Todos" ? "Todos" : getTipoLabel({ tipoServicio: filterTipo })}   |   Filtro compañía: ${filterCompania}   |   Búsqueda: ${query ? query : "sin búsqueda"}`,
    marginX,
    94
  );

  const cardY = 118;
  const cardW = 145;
  const cardH = 64;
  const gap = 12;

  drawMetricCard(
    marginX,
    cardY,
    cardW,
    cardH,
    "Gasto mensual",
    money(resumen.totalMensual),
    "Estimado normalizado"
  );

  drawMetricCard(
    marginX + (cardW + gap),
    cardY,
    cardW,
    cardH,
    "Proyección anual",
    money(resumen.totalAnual),
    "Mensual x 12"
  );

  drawMetricCard(
    marginX + (cardW + gap) * 2,
    cardY,
    cardW,
    cardH,
    "Suscripciones",
    suscripciones.length,
    money(totalSuscripcionesMensual)
  );

  drawMetricCard(
    marginX + (cardW + gap) * 3,
    cardY,
    cardW,
    cardH,
    "Telefonía / Internet",
    telefonia.length,
    money(totalTelefoniaMensual)
  );

  drawMetricCard(
    marginX + (cardW + gap) * 4,
    cardY,
    cardW,
    cardH,
    "Renovan pronto",
    renovaciones.length,
    "Próximos 45 días"
  );

  let y = 215;

  // =========================
  // RESUMEN POR CATEGORÍA
  // =========================
  sectionTitle("Resumen por categoría", y);
  y += 18;

  autoTable(doc, {
    startY: y,
    head: [["Categoría", "Registros", "Mensual estimado", "Proyección anual", "Participación mensual"]],
    body: [
      [
        "Suscripciones digitales",
        String(suscripciones.length),
        money(totalSuscripcionesMensual),
        money(totalSuscripcionesAnual),
        resumen.totalMensual > 0
          ? `${((totalSuscripcionesMensual / resumen.totalMensual) * 100).toFixed(1)}%`
          : "0%",
      ],
      [
        "Telefonía / Internet",
        String(telefonia.length),
        money(totalTelefoniaMensual),
        money(totalTelefoniaAnual),
        resumen.totalMensual > 0
          ? `${((totalTelefoniaMensual / resumen.totalMensual) * 100).toFixed(1)}%`
          : "0%",
      ],
      [
        "TOTAL",
        String(filtered.length),
        money(resumen.totalMensual),
        money(resumen.totalAnual),
        "100%",
      ],
    ],
    styles: {
      font: "helvetica",
      fontSize: 9,
      cellPadding: 7,
      overflow: "linebreak",
      valign: "middle",
    },
    headStyles: {
      fillColor: [17, 24, 39],
      textColor: [255, 255, 255],
      fontStyle: "bold",
    },
    bodyStyles: {
      textColor: [31, 41, 55],
    },
    alternateRowStyles: {
      fillColor: [248, 250, 252],
    },
    columnStyles: {
      1: { halign: "center" },
      2: { halign: "right" },
      3: { halign: "right" },
      4: { halign: "center" },
    },
    margin: { left: marginX, right: marginX },
  });

  y = doc.lastAutoTable.finalY + 30;

  // =========================
  // RENOVACIONES PRÓXIMAS
  // =========================
  if (renovaciones.length > 0) {
    y = checkPageSpace(y, 120);

    sectionTitle(
      "Alertas de renovación",
      y,
      "Servicios con fecha de renovación dentro de los próximos 45 días."
    );

    y += 25;

    autoTable(doc, {
      startY: y,
      head: [["Tipo", "Compañía / Plataforma", "Servicio", "Responsable", "Renovación", "Mensual"]],
      body: renovaciones.map((c) => [
        getTipoLabel(c),
        normalize(c.compania) || "-",
        servicioPrincipal(c),
        responsablePrincipal(c),
        formatDate(c.fechaRenovacion),
        money(calcularCostoMensual(c)),
      ]),
      styles: {
        font: "helvetica",
        fontSize: 8.5,
        cellPadding: 6,
        overflow: "linebreak",
        valign: "middle",
      },
      headStyles: {
        fillColor: [245, 158, 11],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
      alternateRowStyles: {
        fillColor: [255, 251, 235],
      },
      columnStyles: {
        5: { halign: "right" },
      },
      margin: { left: marginX, right: marginX },
    });

    y = doc.lastAutoTable.finalY + 30;
  }

  // =========================
  // SUSCRIPCIONES DIGITALES
  // =========================
  y = checkPageSpace(y, 160);

  sectionTitle(
    "Suscripciones digitales",
    y,
    `Total mensual: ${money(totalSuscripcionesMensual)}   |   Proyección anual: ${money(totalSuscripcionesAnual)}`
  );

  y += 25;

  if (suscripciones.length === 0) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text("No hay suscripciones digitales registradas con los filtros actuales.", marginX, y);
    y += 25;
  } else {
    autoTable(doc, {
      startY: y,
      head: [[
        "Plataforma",
        "Servicio",
        "Plan",
        "Responsable",
        "Periodicidad",
        "Mensual",
        "Anual",
        "Renovación",
        "Estatus",
      ]],
      body: suscripciones.map((c) => [
        normalize(c.compania) || "-",
        servicioPrincipal(c),
        normalize(c.plan) || "-",
        responsablePrincipal(c),
        getPeriodicidad(c),
        money(calcularCostoMensual(c)),
        money(calcularCostoAnual(c)),
        formatDate(c.fechaRenovacion),
        normalize(c.estatus || "ACTIVO"),
      ]),
      styles: {
        font: "helvetica",
        fontSize: 8,
        cellPadding: 5.5,
        overflow: "linebreak",
        valign: "middle",
      },
      headStyles: {
        fillColor: [88, 28, 135],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
      alternateRowStyles: {
        fillColor: [250, 245, 255],
      },
      columnStyles: {
        0: { cellWidth: 85 },
        1: { cellWidth: 145 },
        2: { cellWidth: 90 },
        3: { cellWidth: 95 },
        4: { cellWidth: 70 },
        5: { halign: "right", cellWidth: 70 },
        6: { halign: "right", cellWidth: 75 },
        7: { cellWidth: 75 },
        8: { cellWidth: 65 },
      },
      margin: { left: marginX, right: marginX },
    });

    y = doc.lastAutoTable.finalY + 30;
  }

  // =========================
  // TELEFONÍA / INTERNET
  // =========================
  y = checkPageSpace(y, 160);

  sectionTitle(
    "Telefonía / Internet",
    y,
    `Total mensual: ${money(totalTelefoniaMensual)}   |   Proyección anual: ${money(totalTelefoniaAnual)}`
  );

  y += 25;

  if (telefonia.length === 0) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text("No hay contratos de telefonía o internet con los filtros actuales.", marginX, y);
  } else {
    autoTable(doc, {
      startY: y,
      head: [[
        "Municipio / Ubicación",
        "Oficina",
        "Compañía",
        "Responsable",
        "Contacto",
        "Periodicidad",
        "Mensual",
        "Anual",
        "Fecha pago",
        "Estatus",
      ]],
      body: telefonia.map((c) => [
        normalize(c.ubicacion) || "-",
        normalize(c.oficina) || "-",
        normalize(c.compania) || "-",
        responsablePrincipal(c),
        administradorPrincipal(c),
        getPeriodicidad(c),
        money(calcularCostoMensual(c)),
        money(calcularCostoAnual(c)),
        formatDate(c.fechaPago),
        normalize(c.estatus || "ACTIVO"),
      ]),
      styles: {
        font: "helvetica",
        fontSize: 8,
        cellPadding: 5.5,
        overflow: "linebreak",
        valign: "middle",
      },
      headStyles: {
        fillColor: [30, 64, 175],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
      alternateRowStyles: {
        fillColor: [239, 246, 255],
      },
      columnStyles: {
        0: { cellWidth: 95 },
        1: { cellWidth: 145 },
        2: { cellWidth: 75 },
        3: { cellWidth: 110 },
        4: { cellWidth: 115 },
        5: { cellWidth: 70 },
        6: { halign: "right", cellWidth: 70 },
        7: { halign: "right", cellWidth: 75 },
        8: { cellWidth: 70 },
        9: { cellWidth: 60 },
      },
      margin: { left: marginX, right: marginX },
    });
  }

  drawFooter();

  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i += 1) {
    doc.setPage(i);
    drawFooter();
  }

  doc.save(`contratos_suscripciones_ejecutivo_${filenameStamp()}.pdf`);
};

  const SortIcon = ({ active, dir }) => (
    <span
      className={`ml-1 inline-flex items-center ${
        active ? "text-gray-900" : "text-gray-400"
      }`}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
        <path
          d="M8 9l4-4 4 4"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity={active && dir === "asc" ? 1 : 0.35}
        />
        <path
          d="M16 15l-4 4-4-4"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity={active && dir === "desc" ? 1 : 0.35}
        />
      </svg>
    </span>
  );

  const Th = ({ label, sort }) => {
    const active = sortKey === sort;

    return (
      <th className="px-4 py-3 text-left border-b border-gray-200 whitespace-nowrap">
        {sort ? (
          <button
            type="button"
            onClick={() => toggleSort(sort)}
            className="inline-flex items-center gap-1 text-xs font-semibold tracking-wide uppercase text-gray-600 hover:text-gray-900"
            title="Ordenar"
          >
            {label}
            <SortIcon active={active} dir={sortDir} />
          </button>
        ) : (
          <span className="text-xs font-semibold tracking-wide uppercase text-gray-600">
            {label}
          </span>
        )}
      </th>
    );
  };

  const MetricCard = ({ label, value, sub }) => (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
      <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold">
        {label}
      </div>
      <div className="mt-1 text-xl font-bold text-gray-900">{value}</div>
      {sub && <div className="mt-1 text-xs text-gray-500">{sub}</div>}
    </div>
  );

  return (
    <div className="p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">
            Contratos y Suscripciones
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Control ejecutivo de telefonía, internet, plataformas digitales y
            renovaciones.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Link
            to="/contratos/nuevo"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white shadow-sm transition"
          >
            + Agregar servicio
          </Link>

          <button
            onClick={exportCsv}
            disabled={loading || filtered.length === 0}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 shadow-sm transition disabled:opacity-50"
            title="Exportar a Excel (CSV)"
          >
            <span></span> Exportar Excel
          </button>

          <button
            onClick={exportPdf}
            disabled={loading || filtered.length === 0}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 shadow-sm transition disabled:opacity-50"
            title="Exportar a PDF"
          >
            <span></span> Exportar PDF
          </button>

          <button
            onClick={cargarContratos}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 shadow-sm transition"
            title="Recargar"
          >
            ↻ Recargar
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4">
        <MetricCard
          label="Gasto mensual"
          value={money(resumen.totalMensual)}
          sub="Estimado normalizado"
        />
        <MetricCard
          label="Gasto anual"
          value={money(resumen.totalAnual)}
          sub="Proyección anual"
        />
        <MetricCard
          label="Suscripciones"
          value={resumen.suscripciones}
          sub={money(resumen.totalSuscripcionesMensual)}
        />
        <MetricCard
          label="Telefonía / Internet"
          value={resumen.telefoniaInternet}
          sub={money(resumen.totalTelefoniaMensual)}
        />
        <MetricCard
          label="Renovan pronto"
          value={resumen.proximasRenovaciones}
          sub="Próximos 45 días"
        />
      </div>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 mb-4">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
            <div className="relative w-full lg:w-[420px]">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar por plataforma, servicio, oficina, correo, responsable..."
                className="w-full pl-10 pr-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-300"
              />

              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                🔎
              </span>
            </div>

            <select
              value={filterTipo}
              onChange={(e) => setFilterTipo(e.target.value)}
              className="w-full lg:w-[230px] px-3 py-2 rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-300"
              title="Filtrar por tipo"
            >
              <option value="Todos">Todos los servicios</option>
              <option value={TIPO_TELEFONIA}>Telefonía / Internet</option>
              <option value={TIPO_SUSCRIPCION}>Suscripción digital</option>
            </select>

            <select
              value={filterCompania}
              onChange={(e) => setFilterCompania(e.target.value)}
              className="w-full lg:w-[220px] px-3 py-2 rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-300"
              title="Filtrar por compañía"
            >
              {companias.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>

            {(query || filterCompania !== "Todas" || filterTipo !== "Todos") && (
              <button
                onClick={() => {
                  setQuery("");
                  setFilterCompania("Todas");
                  setFilterTipo("Todos");
                }}
                className="px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 transition"
              >
                Limpiar
              </button>
            )}
          </div>

          <div className="text-sm text-gray-600">
            <span className="font-semibold text-gray-900">{filtered.length}</span>{" "}
            servicios
            <span className="mx-2 text-gray-300">|</span>
            Mensual:{" "}
            <span className="font-semibold text-gray-900">
              {money(resumen.totalMensual)}
            </span>
            <span className="mx-2 text-gray-300">|</span>
            Anual:{" "}
            <span className="font-semibold text-gray-900">
              {money(resumen.totalAnual)}
            </span>
          </div>
        </div>
      </div>

      {errorMsg && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
          {errorMsg}
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm bg-white">
        <table className="min-w-[1450px] w-full text-sm">
          <thead className="bg-gray-50 text-gray-700 sticky top-0 z-10">
            <tr>
              <Th label="Tipo" sort="tipoServicio" />
              <Th label="Compañía" sort="compania" />
              <Th label="Servicio / Oficina" sort="oficina" />
              <Th label="Responsable" sort="propietario" />
              <Th label="Administrador / Contacto" sort="correo" />
              <Th label="Periodicidad" sort="periodicidad" />
              <Th label="Costo mensual" sort="totalPagar" />
              <Th label="Costo anual" sort="totalPagar" />
              <Th label="Pago / Renovación" sort="fechaPago" />
              <Th label="Estatus" sort="estatus" />
              <Th label="No. cuenta / ID" sort="numeroCuenta" />
              <th className="px-4 py-3 text-left border-b border-gray-200 whitespace-nowrap">
                <span className="text-xs font-semibold tracking-wide uppercase text-gray-600">
                  Contraseña
                </span>
              </th>
              <th className="px-4 py-3 text-right border-b border-gray-200 whitespace-nowrap">
                <span className="text-xs font-semibold tracking-wide uppercase text-gray-600">
                  Acciones
                </span>
              </th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i} className="border-b last:border-b-0">
                  {Array.from({ length: 13 }).map((__, j) => (
                    <td key={j} className="px-4 py-3">
                      <div className="h-4 w-full max-w-[180px] bg-gray-100 rounded animate-pulse" />
                    </td>
                  ))}
                </tr>
              ))
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={13} className="px-6 py-12 text-center">
                  <div className="text-gray-900 font-semibold">
                    No hay servicios para mostrar
                  </div>
                  <div className="text-gray-500 text-sm mt-1">
                    Prueba cambiando filtros o búsqueda.
                  </div>
                </td>
              </tr>
            ) : (
              filtered.map((c, idx) => {
                const isRevealed = revealed.has(c.id);
                const rowAlt = idx % 2 === 0 ? "bg-white" : "bg-gray-50/50";
                const tipo = getTipoServicio(c);
                const renovar = estaPorRenovar(c);

                return (
                  <tr
                    key={c.id}
                    className={`border-b last:border-b-0 ${rowAlt} hover:bg-blue-50/40 transition`}
                  >
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center rounded-lg px-2.5 py-1 font-semibold border ${
                          tipo === TIPO_SUSCRIPCION
                            ? "bg-purple-50 text-purple-700 border-purple-100"
                            : "bg-blue-50 text-blue-700 border-blue-100"
                        }`}
                      >
                        {getTipoLabel(c)}
                      </span>
                    </td>

                    <td className="px-4 py-3">
                      <span className="inline-flex items-center rounded-lg bg-gray-50 text-gray-800 border border-gray-200 px-2.5 py-1 font-semibold">
                        {c.compania || "-"}
                      </span>
                    </td>

                    <td className="px-4 py-3 max-w-[260px]">
                      <div className="font-semibold text-gray-900">
                        {servicioPrincipal(c)}
                      </div>

                      {normalize(c.plan) && (
                        <div className="text-xs text-gray-500 mt-0.5">{c.plan}</div>
                      )}

                      {tipo === TIPO_TELEFONIA && normalize(c.ubicacion) && (
                        <div className="text-xs text-gray-500 mt-0.5">{c.ubicacion}</div>
                      )}
                    </td>

                    <td className="px-4 py-3 text-gray-700">
                      {responsablePrincipal(c)}
                    </td>

                    <td className="px-4 py-3 max-w-[230px]">
                      <span
                        className="block truncate text-gray-700"
                        title={administradorPrincipal(c)}
                      >
                        {administradorPrincipal(c)}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-gray-700 capitalize">
                      {getPeriodicidad(c)}
                    </td>

                    <td className="px-4 py-3">
                      <span className="inline-flex items-center rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-100 px-2.5 py-1 font-semibold">
                        {money(calcularCostoMensual(c))}
                      </span>
                    </td>

                    <td className="px-4 py-3">
                      <span className="inline-flex items-center rounded-lg bg-slate-50 text-slate-700 border border-slate-200 px-2.5 py-1 font-semibold">
                        {money(calcularCostoAnual(c))}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-gray-700">
                      <div>{formatDate(fechaClave(c))}</div>
                      {renovar && (
                        <div className="text-xs text-orange-700 font-semibold mt-1">
                          Renovación próxima
                        </div>
                      )}
                    </td>

                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center rounded-lg px-2.5 py-1 font-semibold border ${
                          normalize(c.estatus || "ACTIVO") === "ACTIVO"
                            ? "bg-green-50 text-green-700 border-green-100"
                            : "bg-orange-50 text-orange-700 border-orange-100"
                        }`}
                      >
                        {c.estatus || "ACTIVO"}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-gray-700">
                      {c.numeroCuenta || "-"}
                    </td>

                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span
                          className={`font-mono text-xs ${
                            isRevealed ? "text-gray-900" : "text-gray-400"
                          }`}
                          title={isRevealed ? c.contrasena : "Oculta"}
                        >
                          {normalize(c.contrasena)
                            ? isRevealed
                              ? c.contrasena
                              : "••••••••"
                            : "-"}
                        </span>

                        {normalize(c.contrasena) && (
                          <button
                            type="button"
                            onClick={() => toggleReveal(c.id)}
                            className="text-xs px-2 py-1 rounded-md border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 transition"
                            title={isRevealed ? "Ocultar" : "Mostrar"}
                          >
                            {isRevealed ? "Ocultar" : "Mostrar"}
                          </button>
                        )}
                      </div>
                    </td>

                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-3">
                        <Link
                          to={`/contratos/editar/${c.id}`}
                          className="text-blue-700 hover:text-blue-900 font-semibold"
                        >
                          Editar
                        </Link>

                        <button
                          onClick={() => eliminar(c.id)}
                          disabled={deletingId === c.id}
                          className="text-red-700 hover:text-red-900 font-semibold disabled:opacity-50"
                        >
                          {deletingId === c.id ? "Eliminando..." : "Eliminar"}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-3 text-xs text-gray-500">
        Tip: el gasto mensual se normaliza según periodicidad. Ejemplo: anual / 12,
        semestral / 6, trimestral / 3.
      </div>
    </div>
  );
}